import { useEffect } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { X } from "lucide-react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import type CreateEmailMessageViewModel from "../../types/viewModels/CreateEmailMessageViewModel";
import type { ExecutionResponse } from "@repo/types/responses";
import type EmailReplyProps from "../../types/ui/EmailReplyProps";
import { useGetLinkedAccounts, useSendEmailMessage } from "../../hooks/queries";
import { useDialog } from "../../hooks/contexts/useDialog";
import TextEditor from "../ui/textEditor/TextEditor";
import TruncatedText from "../ui/TruncatedText";
import { SelectDropdown } from "../ui/dropdown/SelectDropdown";
import Button from "../ui/button/Button";
import EmailReply from "./EmailReply";

export default function EmailReplyContainer({
  ticket,
  replyToEmailMessage,
  onClose,
}: EmailReplyProps) {
  const dialog = useDialog();
  const { mutateAsync: sendEmail } = useSendEmailMessage(ticket.TicketGuid);

  const { data: linkedAccounts, isPending: isLoadingLinkedAccounts } =
    useGetLinkedAccounts((la) =>
      la.map((account) => ({
        label:
          account.Email.length > 20
            ? `${account.Email.substring(0, 20)}...`
            : account.Email,
        value: account.Email,
      })),
    );

  const handleEmailReply = async (
    model: CreateEmailMessageViewModel,
  ): Promise<ExecutionResponse> => {
    const res = await sendEmail({
      model,
    });
    if (res) {
      dialog.show({
        title: "Email Sent Status",
        message: res.Message,
        type: res.Result ? "success" : "error",
        confirmText: "Ok",
      });
      onClose();
    }

    return res;
  };

  const methods = useForm<CreateEmailMessageViewModel>({
    defaultValues: {
      Content: "",
      Mentions: [],
      Attachments: [],
      From: { Email: "", Name: "" },
      ToRecipients: [],
      CcRecipients: [],
      BccRecipients: [],
      Subject: `Re: ${ticket.Title}`,
      TicketId: ticket.Id,
    },
  });

  useEffect(() => {
    if (isLoadingLinkedAccounts || !linkedAccounts?.[0] || !replyToEmailMessage)
      return;

    const model: CreateEmailMessageViewModel = {
      Content: "",
      Mentions: [],
      Attachments: [],
      MessageId: replyToEmailMessage?.MessageId,
      ConversationId: replyToEmailMessage?.ConversationId,
      From: {
        Email: linkedAccounts?.[0].value || "",
      },
      ToRecipients: [
        {
          Name: replyToEmailMessage?.FromName,
          Email: replyToEmailMessage?.FromEmail,
        },
      ],
      CcRecipients:
        replyToEmailMessage?.CcEmails && replyToEmailMessage.CcEmails.length > 0
          ? [
              {
                Name: replyToEmailMessage?.CcEmails[0]?.Name || "",
                Email: replyToEmailMessage?.CcEmails[0]?.Email || "",
              },
            ]
          : null,

      BccRecipients:
        replyToEmailMessage?.BccEmails &&
        replyToEmailMessage.BccEmails.length > 0
          ? [
              {
                Name: replyToEmailMessage?.BccEmails[0]?.Name || "",
                Email: replyToEmailMessage?.BccEmails[0]?.Email || "",
              },
            ]
          : null,
      Subject: replyToEmailMessage.Subject,
      TicketId: ticket.Id,
    };

    methods.reset(model);
  }, [
    isLoadingLinkedAccounts,
    linkedAccounts,
    replyToEmailMessage,
    ticket.Id,
    methods,
  ]);

  return (
    <Dialog open={true} onClose={onClose}>
      <div
        className={
          "fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3"
        }
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30" />

        {/* Floating Panel */}
        <DialogPanel className="fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3">
          <FormProvider {...methods}>
            <div className="w-195 bg-white rounded-t shadow-xl border border-gray-200 overflow-hidden">
              <DialogTitle className="flex w-full items-center text-lg p-2 bg-gray-50 text-gray-700 truncate">
                <div className="basis-[96%]">
                  <div className="flex w-full items-center justify-between space-x-1 text-base text-gray-600">
                    <div className="">
                      <TruncatedText
                        text={`Re: ${ticket.Title}`}
                        maxLength={50}
                      />
                    </div>

                    {!isLoadingLinkedAccounts &&
                      linkedAccounts &&
                      linkedAccounts.length > 0 && (
                        <div className="">
                          <Controller
                            name="From"
                            control={methods.control}
                            rules={{
                              required: "From is required",
                            }}
                            render={({ field }) => (
                              <SelectDropdown
                                items={linkedAccounts}
                                value={field.value?.Email ?? ""}
                                onChange={(newAddress: string | null) => {
                                  field.onChange({
                                    ...(field.value ?? {
                                      Name: "",
                                      Address: "",
                                    }),
                                    Address: newAddress,
                                  });
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                  </div>
                </div>
                <div className="basis-[4%] ml-2">
                  <Button
                    variant="ghost"
                    onClick={onClose}
                    className="hover:text-gray-700 cursor-pointer hover:bg-gray-200 rounded ml-auto"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </DialogTitle>

              <TextEditor
                mentionsEnabled={false}
                templatesEnabled={true}
                attachmentsEnabled={true}
                submitButtonText="Send"
                onSubmit={methods.handleSubmit(handleEmailReply)}
              >
                <EmailReply />
              </TextEditor>
            </div>
          </FormProvider>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
